import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import { marketsAPI } from '../../utils/api';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import CategoryFilter from '../../components/CategoryFilter';
import Sparkline from '../../components/Sparkline';
import { Search } from 'lucide-react';

const MarketsNew = () => {
  const [markets, setMarkets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');

  const categories = [
    'All',
    'DeFi',
    'NFT',
    'Metaverse',
    'Gaming',
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const marketsRes = await marketsAPI.getCryptocurrencies();
      setMarkets(marketsRes.data || []);
    } catch (error) {
      console.error('Market data fetch error:', error);
      toast.error('Failed to load market data');
    } finally {
      setLoading(false);
    }
  };

  const categoryKeywords = {
    DeFi: [
      'uni', 'uniswap', 'aave', 'maker', 'mkr', 'compound', 'comp',
      'curve', 'crv', 'sushi', 'sushiswap', 'synthetix', 'snx',
      'yearn', 'yfi', 'lido', 'ldo', 'pancakeswap', 'cake',
    ],
    NFT: [
      'ape', 'apecoin', 'mana', 'sand', 'axs', 'immutable', 'imx',
      'enjin', 'enj', 'flow', 'theta', 'chiliz', 'chz',
    ],
    Metaverse: [
      'metaverse', 'decentraland', 'mana', 'sandbox', 'sand',
      'render', 'rndr', 'gala',
    ],
    Gaming: [
      'game', 'gaming', 'gala', 'axie', 'axs', 'enjin', 'enj',
      'immutable', 'imx', 'illuvium', 'ilv', 'beam',
    ],
  };

  const matchesCategory = (market, category) => {
    if (category === 'All') return true;

    const searchable = `${market.id || ''} ${market.name || ''} ${market.symbol || ''}`.toLowerCase();
    const keywords = categoryKeywords[category] || [];

    return keywords.some((keyword) => searchable.includes(keyword));
  };

  const filteredMarkets = markets.filter(
    (market) => {
      const query = searchQuery.toLowerCase().trim();
      const matchesSearch =
        !query ||
        market.name?.toLowerCase().includes(query) ||
        market.symbol?.toLowerCase().includes(query) ||
        market.id?.toLowerCase().includes(query);

      return matchesSearch && matchesCategory(market, activeCategory);
    }
  );

  const formatCurrency = (value) => {
    if (value >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
    if (value >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
    if (value >= 1e3) return `$${(value / 1e3).toFixed(2)}K`;
    return `$${value?.toFixed(2)}`;
  };

  const generateSparklineData = (seedKey, isPositive) => {
    const baseSeed = (seedKey || 'default')
      .split('')
      .reduce((acc, char) => acc + char.charCodeAt(0), 0);

    const seededRandom = (seed) => {
      const x = Math.sin(seed) * 10000;
      return x - Math.floor(x);
    };

    return Array.from({ length: 7 }, (_, index) => {
      const trend = isPositive ? index * 4 : -index * 3;
      const noise = (seededRandom(baseSeed + index * 11.73) - 0.5) * 10;
      return 75 + trend + noise;
    });
  };

  const fallbackSparklineByCoin = useMemo(() => {
    const mapped = {};
    filteredMarkets.forEach((coin) => {
      const change24h = coin.price_change_percentage_24h || 0;
      mapped[coin.id] = generateSparklineData(coin.id, change24h >= 0);
    });
    return mapped;
  }, [filteredMarkets]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
          .loading-spinner {
            width: 48px;
            height: 48px;
            border: 4px solid #e5e7eb;
            border-top-color: #062136;
            border-radius: 50%;
            animation: spin 1s linear infinite;
          }
        `}</style>
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div
      className="space-y-6 p-4 md:p-6 lg:pl-10 lg:pr-6 lg:py-10 animate-fadeIn bg-[#F9F9F9]"
      data-testid="markets-page"
    >
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
          Market
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1 text-sm md:text-base">
          Live cryptocurrency prices and market data
        </p>
      </div>

      <div className="flex flex-col md:flex-row gap-4 items-center w-full justify-between">
        {/* Category Filters */}
        <div className="order-2 md:order-1 w-full md:w-auto">
          <CategoryFilter
            categories={categories}
            activeCategory={activeCategory}
            onCategoryChange={setActiveCategory}
          />
        </div>

        {/* Search Bar */}
        <Card className="order-1 md:order-2 p-4 md:ml-auto bg-transparent shadow-none border-none w-full md:w-auto">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              type="text"
              placeholder="Search coins..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 w-full md:w-auto"
            />
          </div>
        </Card>
      </div>

      {/* Market Table */}
      {filteredMarkets.length > 0 ? (
        <Card className="p-6 overflow-hidden">
          <div className="responsive-table-wrapper">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left text-sm font-medium text-gray-600 dark:text-gray-400 pb-3">
                    #
                  </th>
                  <th className="text-left text-sm font-medium text-gray-600 dark:text-gray-400 pb-3">
                    Name
                  </th>
                  <th className="text-left text-sm font-medium text-gray-600 dark:text-gray-400 pb-3">
                    Price
                  </th>
                  <th className="text-left text-sm font-medium text-gray-600 dark:text-gray-400 pb-3">
                    24h %
                  </th>
                  <th className="text-left text-sm font-medium text-gray-600 dark:text-gray-400 pb-3">
                    Volume
                  </th>
                  <th className="text-left text-sm font-medium text-gray-600 dark:text-gray-400 pb-3">
                    Market Cap
                  </th>
                  <th className="text-left text-sm font-medium text-gray-600 dark:text-gray-400 pb-3">
                    Last 7 Days
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredMarkets.map((coin, idx) => {
                  const change24h = coin.price_change_percentage_24h || 0;
                  const isPositive = change24h >= 0;
                  // Dynamically set sparkline color based on theme
                  let sparklineColor = '#062136';
                  if (typeof window !== 'undefined') {
                    if (document.documentElement.classList.contains('dark')) {
                      sparklineColor = '#21bcfe';
                    }
                  }
                  return (
                    <tr
                      key={coin.id}
                      className="border-b border-gray-100 dark:border-gray-800 last:border-0 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                    >
                      <td className="py-4 text-gray-600 dark:text-gray-400">
                        {coin.market_cap_rank}
                      </td>
                      <td className="py-4">
                        <div className="flex items-center space-x-3">
                          <img
                            src={coin.image}
                            alt={coin.name}
                            className="w-8 h-8 rounded-full"
                          />
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white">
                              {coin.name}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400 uppercase">
                              {coin.symbol}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 text-gray-900 dark:text-white font-medium">
                        {formatCurrency(coin.current_price)}
                      </td>
                      <td className="py-4">
                        <span
                          className={`font-medium ${isPositive ? 'text-green-600' : 'text-red-600'
                            }`}
                        >
                          {isPositive ? '▲' : '▼'}{' '}
                          {Math.abs(change24h).toFixed(2)}%
                        </span>
                      </td>
                      <td className="py-4 text-gray-900 dark:text-white">
                        {formatCurrency(coin.total_volume)}
                      </td>
                      <td className="py-4 text-gray-900 dark:text-white">
                        {formatCurrency(coin.market_cap)}
                      </td>
                      <td className="py-4">
                        <Sparkline
                          data={
                            coin.sparkline_in_7d?.price ||
                            fallbackSparklineByCoin[coin.id] || []
                          }
                          color={sparklineColor}
                          width={100}
                          height={40}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      ) : (
        <Card className="p-12 text-center">
          <p className="text-gray-500 dark:text-gray-400">
            No cryptocurrencies found
          </p>
          <button
            onClick={fetchData}
            className="mt-4 text-blue-600 hover:text-blue-700 font-medium"
          >
            Retry
          </button>
        </Card>
      )}
    </div>
  );
};

export default MarketsNew;
